import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { Row, Form, Col, Input } from 'reactstrap';
import ReactButton from 'components/Buttons/ReactButton';
import Loader from 'components/Loader';
import { ScreenPrompt } from 'components/ScreenPrompt';
import FlexSelect from 'components/Inputs/FlexSelect';
import { CodeBlock } from 'components/FlexCodeBlock';
import useWebhookForm from './hooks/useWebhookForm';

const WebhookForm = (webhook) => {
  const {
    index,
    title,
    payload,
    entity,
    whiteList,
    recordType,
    isSubmiting,
    selectedEntity,
    selectedWhiteList,
    selectedRecordType,
    control,
    handleSubmit,
    errors,
    setValue,
    formState,
    isLoadingCustomFields,
    isLoadingLeadStages,
    isLoadingOpportunityStages,
    errorFormMessage,
    cancelAction,
    onSubmit,
    setTitle,
    setSelectedEntity,
    setSelectedRecordType,
    setSelectedWhiteList
  } = useWebhookForm(webhook);

  const isDirty = formState.isDirty;
  const formattedTitle = title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, '_');

  const url = `${process.env.REACT_APP_PROXY_URL}crm-service/api/webhook/${formattedTitle}`;
  useEffect(() => {
    if (webhook && Object.keys(webhook.webHook).length > 0) {
      setTitle(webhook?.webHook?.name);
      setValue('name', webhook?.webHook?.name);
      setSelectedEntity(webhook?.webHook?.entity);
      setValue('entity', webhook?.webHook?.entity);
      setSelectedRecordType(webhook?.webHook?.recordType?.id);
      setValue('fkRecordTypeId', webhook?.webHook?.recordType?.id);
      if (
        webhook?.webHook?.webhook_whitelist &&
        webhook?.webHook?.webhook_whitelist.length > 0
      ) {
        const whiteListIds = webhook?.webHook?.webhook_whitelist.map(
          (item) => item.whiteList.id
        );
        setSelectedWhiteList(whiteListIds);
        setValue('whiteListIds', whiteListIds);
      }
    }
  }, []);
  return (
    <>
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <Form className="needs-validation" onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col sm="4" className="mb-3">
            <label className="form-control-label text-capitalize">
              Título*
            </label>
            <Controller
              render={({ field }) => (
                <Input
                  {...field}
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setValue('name', e.target.value);
                  }}
                  type="text"
                  placeholder="Título"
                />
              )}
              control={control}
              defaultValue={webhook.name || ''}
              rules={{ required: 'Campo obrigatório' }}
              name="name"
            />
            <ErrorMessage
              errors={errors}
              name="name"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
          <Col sm="8" className="mb-3">
            <label className="form-control-label text-capitalize">URL</label>
            <Controller
              render={({ field }) => (
                <Input
                  {...field}
                  value={url}
                  type="text"
                  placeholder="URL"
                  readOnly
                />
              )}
              control={control}
              defaultValue=""
              name="url"
            />
            <ErrorMessage
              errors={errors}
              name="url"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
        </Row>
        <Row>
          <Col sm="4" className="mb-3">
            <label className="form-control-label text-capitalize">
              Entidade*
            </label>
            <Controller
              control={control}
              name="entity"
              defaultValue=""
              render={({ field }) => (
                <FlexSelect
                  {...field}
                  isClearable={true}
                  dataOptions={entity || []}
                  isMulti={false}
                  closeMenuOnSelect={true}
                  value={selectedEntity}
                  onChange={(selected) => {
                    setSelectedEntity(selected);
                    setValue('entity', selected);
                  }}
                  valueController={setValue}
                  fieldName="entity"
                  labelName="name"
                  valueName="id"
                  defaultValue={webhook?.webHook?.entity || ''}
                />
              )}
              rules={{
                required: 'A Entidade é obrigatória!'
              }}
            />
            <ErrorMessage
              errors={errors}
              name="entity"
              render={({ message }) => (
                <p style={{ color: 'red' }}>{message}</p>
              )}
            />
          </Col>
          <Col sm="4" className="mb-3">
            <label className="form-control-label text-capitalize">
              Tipo de Registro*
            </label>
            <Controller
              control={control}
              name="fkRecordTypeId"
              defaultValue=""
              render={({ field }) => (
                <FlexSelect
                  {...field}
                  isClearable={true}
                  dataOptions={recordType || []}
                  isMulti={false}
                  closeMenuOnSelect={true}
                  value={selectedRecordType}
                  onChange={(selected) => {
                    setSelectedRecordType(selected);
                    setValue('fkRecordTypeId', selected);
                  }}
                  valueController={setValue}
                  fieldName="fkRecordTypeId"
                  labelName="name"
                  valueName="id"
                  defaultValue={webhook?.fkRecordTypeId || ''}
                />
              )}
              rules={{
                required: 'O Tipo de Registro é obrigatório!'
              }}
            />
            <ErrorMessage
              errors={errors}
              name="fkRecordTypeId"
              render={({ message }) => (
                <p style={{ color: 'red' }}>{message}</p>
              )}
            />
          </Col>
          <Col sm="4" className="mb-3">
            <label className="form-control-label text-capitalize">
              WhiteList
            </label>
            <Controller
              control={control}
              name="whiteListIds"
              defaultValue={[]}
              render={({ field }) => (
                <FlexSelect
                  {...field}
                  isClearable={true}
                  dataOptions={whiteList || []}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  value={selectedWhiteList}
                  onChange={(selected) => {
                    setSelectedWhiteList(selected);
                    setValue('whiteListIds', selected);
                  }}
                  valueController={setValue}
                  fieldName="whiteListIds"
                  labelName="item"
                  valueName="id"
                  defaultValue={webhook?.whiteListIds || ''}
                />
              )}
            />
            <ErrorMessage
              errors={errors}
              name="whiteListIds"
              render={({ message }) => (
                <p style={{ color: 'red' }}>{message}</p>
              )}
            />
          </Col>
        </Row>
        <Row>
          {isLoadingOpportunityStages ||
          isLoadingCustomFields ||
          isLoadingLeadStages ? (
            <Col sm="12" className="mb-4">
              <Row className="justify-content-md-center">
                <Loader />
              </Row>
            </Col>
          ) : (
            <>
              <Col sm="8" className="mb-3">
                <CodeBlock code={payload} language="json" caption="Payload" />
              </Col>
              <Col sm="4" className="mb-3">
                <CodeBlock code={index} caption="Indice de Campos" />
              </Col>
            </>
          )}
        </Row>
        <ReactButton
          btnColor="confirmation"
          type="submit"
          disabled={isSubmiting}
        >
          Salvar
        </ReactButton>

        <ReactButton
          btnColor="cancelation"
          type="button"
          onClick={cancelAction}
        >
          Cancelar
        </ReactButton>
      </Form>
    </>
  );
};

export default WebhookForm;
